import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { requestPost, refreshAccessToken } from "../config/Api";
import { Backdrop, CircularProgress, Box, Typography } from "@mui/material";
import {
  getActiveBranches,
  setActiveBranches as persistActiveBranches,
  clearActiveBranches,
} from "../services/ActiveBranchService";

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const refreshTimeoutRef = useRef(null);
  const [auth, setAuth] = useState({
    token: localStorage.getItem("token"),
    user: null,
    loading: true,
    activeBranches: getActiveBranches(),
  });

  useEffect(() => {
    const bootstrap = async () => {
      const token = localStorage.getItem("token");

      if (!token) {
        clearActiveBranches();
        setAuth({
          token: null,
          user: null,
          loading: false,
          activeBranches: [],
        });
        return;
      }

      try {
        const res = await requestPost("/me");
        const persistedToken = localStorage.getItem("token");

        setAuth({
          token: persistedToken || token,
          user: res.data,
          loading: false,
          activeBranches: getActiveBranches(),
        });
      } catch {
        localStorage.removeItem("token");
        localStorage.removeItem("token_expires_at");
        clearActiveBranches();

        setAuth({
          token: null,
          user: null,
          loading: false,
          activeBranches: [],
        });
      }
    };

    bootstrap();
  }, []);

  const login = (token, user, expiresAt) => {
    localStorage.setItem("token", token);
    if (expiresAt) {
      localStorage.setItem("token_expires_at", expiresAt);
    }
    clearActiveBranches();

    setAuth({
      token,
      user,
      loading: false,
      activeBranches: [],
    });
  };

  const logout = () => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }

    localStorage.removeItem("token");
    localStorage.removeItem("token_expires_at");
    clearActiveBranches();

    setAuth({
      token: null,
      user: null,
      loading: false,
      activeBranches: [],
    });
  };

  const setActiveBranches = (branches) => {
    const safeBranches = Array.isArray(branches) ? branches : [];
    persistActiveBranches(safeBranches);

    setAuth((prev) => ({
      ...prev,
      activeBranches: safeBranches,
    }));
  };

  const updateToken = (token) => {
    localStorage.setItem("token", token);

    setAuth((prev) => ({
      ...prev,
      token,
    }));
  };

  const refreshSession = useCallback(async () => {
    const newToken = await refreshAccessToken();

    setAuth((prev) => ({
      ...prev,
      token: newToken,
    }));

    return newToken;
  }, []);

  useEffect(() => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }

    if (!auth.token) {
      return undefined;
    }

    const expiresAt = localStorage.getItem("token_expires_at");

    if (!expiresAt) {
      return undefined;
    }

    const msUntilExpiry = new Date(expiresAt).getTime() - Date.now();

    if (msUntilExpiry <= 0) {
      logout();
      return undefined;
    }

    refreshTimeoutRef.current = setTimeout(() => {
      logout();
    }, msUntilExpiry);

    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.token]);

  const isAuthenticated = () => !!auth.token;

  const hasPermission = (permission) => {
    if (!auth.user || !auth.user.permissions) return false;
    return auth.user.permissions.includes(permission);
  };

  const hasRole = (role) => {
    if (!auth.user || !auth.user.roles) return false;
    return auth.user.roles.includes(role);
  };

  // --- Configuración del sistema (exportada por /login y /me) ---
  const settings = auth.user?.settings || {};
  const onlineQueriesEnabled = settings.online_queries_enabled === true;

  // Combina permiso + kill-switch de consultas en línea. Usar en todo botón
  // que dispare una consulta al Poder Judicial.
  const canQueryOnline = (permission) =>
    hasPermission(permission) && onlineQueriesEnabled;

  // Actualiza en memoria las settings públicas tras un cambio en Configuración.
  const setPublicSettings = (next) =>
    setAuth((prev) => ({
      ...prev,
      user: prev.user
        ? { ...prev.user, settings: { ...(prev.user.settings || {}), ...next } }
        : prev.user,
    }));

  return (
    <AuthContext.Provider
      value={{
        auth,
        login,
        logout,
        isAuthenticated,
        hasPermission,
        hasRole,
        setActiveBranches,
        updateToken,
        refreshSession,
        settings,
        onlineQueriesEnabled,
        canQueryOnline,
        setPublicSettings,
      }}
    >
      {auth.loading ? (
        <Backdrop
          open
          sx={(theme) => ({
            zIndex: theme.zIndex.modal + 10,
            background:
              theme.palette.mode === "dark"
                ? "rgba(0,0,0,0.72)"
                : "rgba(15, 23, 42, 0.22)",
            backdropFilter: "blur(14px)",
            WebkitBackdropFilter: "blur(14px)",
          })}
        >
          <Box
            sx={(theme) => ({
              minWidth: 220,
              px: 3,
              py: 3,
              borderRadius: 4,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.4,
              background:
                theme.palette.mode === "dark"
                  ? "rgba(15,23,42,0.92)"
                  : "rgba(255,255,255,0.72)",
              border:
                theme.palette.mode === "dark"
                  ? "1px solid rgba(255,255,255,0.10)"
                  : "1px solid rgba(255,255,255,0.45)",
              boxShadow: "0 18px 40px rgba(0,0,0,0.14)",
              backdropFilter: "blur(20px)",
              WebkitBackdropFilter: "blur(20px)",
            })}
          >
            <Typography
              sx={(theme) => ({
                fontWeight: 1000,
                fontSize: 28,
                lineHeight: 1,
                letterSpacing: 3,
                color: theme.palette.primary.main,
                animation: "jexBlink 1.8s ease-in-out infinite",
                "@keyframes jexBlink": {
                  "0%": { opacity: 0.35, transform: "scale(0.98)" },
                  "50%": { opacity: 1, transform: "scale(1)" },
                  "100%": { opacity: 0.35, transform: "scale(0.98)" },
                },
              })}
            >
              JEX
            </Typography>

            <CircularProgress
              size={36}
              thickness={4}
              sx={(theme) => ({
                color: theme.palette.primary.main,
              })}
            />

            <Typography
              sx={{
                fontWeight: 900,
                fontSize: 14,
                color: "text.primary",
                letterSpacing: 0.2,
              }}
            >
              Procesando...
            </Typography>
          </Box>
        </Backdrop>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};
